/**
 * Rich text. `CollabEditor` is multiplayer (Yjs over /ws/doc) and saves a
 * JSON + plain-text snapshot back to the entity so search, the API and
 * notifications see the text. `CommentEditor` is a local editor for
 * comments and updates.
 */
import { UserAvatar } from "@/components/avatar";
import { SlashCommands } from "@/components/slash-menu";
import { api } from "@/lib/api";
import { DocConnection, colorFor } from "@/lib/collab";
import { useMe } from "@/lib/hooks";
import { getTable } from "@/lib/store";
import type { RichText } from "@/lib/types";
import { cn } from "@/lib/utils";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import { EditorContent, ReactRenderer, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import type { SuggestionOptions, SuggestionProps } from "@tiptap/suggestion";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { toast } from "sonner";

// ---- @mentions ----------------------------------------------------------------------------

interface MentionItem {
  id: string;
  label: string;
}

const MentionList = forwardRef<{ onKeyDown: (e: KeyboardEvent) => boolean }, SuggestionProps<MentionItem>>(function MentionList(props, ref) {
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [props.items]);
  useImperativeHandle(ref, () => ({
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        setIndex((i) => (i + 1) % Math.max(1, props.items.length));
        return true;
      }
      if (e.key === "ArrowUp") {
        setIndex((i) => (i - 1 + props.items.length) % Math.max(1, props.items.length));
        return true;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        const item = props.items[index];
        if (item) props.command(item);
        return true;
      }
      return false;
    },
  }));
  if (!props.items.length) return null;
  return (
    <div className="w-56 overflow-hidden rounded-lg border bg-popover p-1 text-popover-foreground shadow-md">
      {props.items.map((item, i) => (
        <button key={item.id} type="button" onMouseDown={(e) => (e.preventDefault(), props.command(item))} className={cn("flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px]", i === index && "bg-accent")}>
          <UserAvatar userId={item.id} size="sm" />
          {item.label}
        </button>
      ))}
    </div>
  );
});

const mentionSuggestion: Omit<SuggestionOptions<MentionItem>, "editor"> = {
  char: "@",
  items: ({ query }) =>
    Object.values(getTable("user"))
      .filter((u) => u.active && u.name.toLowerCase().includes(query.toLowerCase()))
      .slice(0, 8)
      .map((u) => ({ id: u.id, label: u.name })),
  render: () => {
    let renderer: ReactRenderer<{ onKeyDown: (e: KeyboardEvent) => boolean }, SuggestionProps<MentionItem>> | null = null;
    let el: HTMLDivElement | null = null;
    const place = (props: SuggestionProps<MentionItem>) => {
      const r = props.clientRect?.();
      if (!r || !el) return;
      el.style.left = `${Math.min(r.left, window.innerWidth - 240)}px`;
      el.style.top = `${r.bottom + 6}px`;
    };
    return {
      onStart: (props) => {
        el = document.createElement("div");
        el.style.position = "fixed";
        el.style.zIndex = "60";
        document.body.appendChild(el);
        renderer = new ReactRenderer(MentionList, { props, editor: props.editor });
        el.appendChild(renderer.element);
        place(props);
      },
      onUpdate: (props) => {
        renderer?.updateProps(props);
        place(props);
      },
      onKeyDown: ({ event }) => {
        if (event.key === "Escape") return false;
        return renderer?.ref?.onKeyDown(event) ?? false;
      },
      onExit: () => {
        renderer?.destroy();
        el?.remove();
        renderer = null;
        el = null;
      },
    };
  },
};

// ---- uploads ----------------------------------------------------------------------------------

export async function uploadFile(file: File, target?: { issueId?: string; projectId?: string; ideaId?: string }): Promise<string> {
  const form = new FormData();
  if (target?.issueId) form.append("issueId", target.issueId);
  if (target?.projectId) form.append("projectId", target.projectId);
  if (target?.ideaId) form.append("ideaId", target.ideaId);
  form.append("file", file);
  const res = await api<{ url: string }>("/api/uploads", { method: "POST", body: form });
  return res.url;
}

function pasteImages(target?: { issueId?: string; projectId?: string; ideaId?: string }) {
  return (editor: Editor, files: File[]) => {
    for (const file of files) {
      if (!file.type.startsWith("image/")) continue;
      const pending = toast.loading(`Uploading ${file.name}…`);
      uploadFile(file, target)
        .then((src) => {
          editor.chain().focus().setImage({ src, alt: file.name }).run();
          toast.dismiss(pending);
        })
        .catch((e) => toast.error(e.message, { id: pending }));
    }
  };
}

export function baseExtensions(placeholder: string, collaborative: boolean, opts: { slash?: boolean } = {}) {
  return [
    StarterKit.configure({ undoRedo: collaborative ? false : undefined, link: false, heading: { levels: [1, 2, 3] } }),
    Placeholder.configure({ placeholder }),
    TaskList,
    TaskItem.configure({ nested: true }),
    Link.configure({ openOnClick: true, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
    Image,
    Mention.configure({ HTMLAttributes: { class: "mention" }, suggestion: mentionSuggestion, renderText: ({ node }) => `@${node.attrs.label}` }),
    ...(opts.slash === false ? [] : [SlashCommands]),
  ];
}

// ---- collaborative editor --------------------------------------------------------------------

export interface CollabEditorHandle {
  editor: Editor | null;
}

interface CollabProps {
  docKey: string;
  /** Stored content used to fill the shared text the first time anyone opens it. */
  seed: RichText;
  onSnapshot: (json: RichText, text: string) => void;
  placeholder?: string;
  className?: string;
  uploadTarget?: { issueId?: string; projectId?: string; ideaId?: string };
  editable?: boolean;
}

export const CollabEditor = forwardRef<CollabEditorHandle, CollabProps>(function CollabEditor(props, ref) {
  const [generation, setGeneration] = useState(0);
  return <CollabEditorInner key={`${props.docKey}:${generation}`} {...props} ref={ref} onReset={() => setGeneration((g) => g + 1)} />;
});

/** Owns the connection's lifetime (created in an effect so React's dev double-mount can't reuse a closed one). */
const CollabEditorInner = forwardRef<CollabEditorHandle, CollabProps & { onReset: () => void }>(function CollabEditorInner(props, ref) {
  const me = useMe().data?.user;
  const [conn, setConn] = useState<DocConnection | null>(null);
  const resetRef = useRef(props.onReset);
  resetRef.current = props.onReset;
  useEffect(() => {
    const c = new DocConnection(props.docKey, { name: me?.name ?? "Someone", color: colorFor(me?.id ?? "x") });
    c.onReset = () => resetRef.current();
    setConn(c);
    return () => {
      c.destroy();
      setConn(null);
    };
  }, [props.docKey]);
  if (!conn) return <div className={cn("relative min-h-8 animate-soft-pulse rounded-md bg-muted/40", props.className)} aria-label="Loading text" />;
  return <CollabEditorBound {...props} conn={conn} user={{ name: me?.name ?? "Someone", color: colorFor(me?.id ?? "x") }} ref={ref} />;
});

const CollabEditorBound = forwardRef<CollabEditorHandle, CollabProps & { conn: DocConnection; user: { name: string; color: string } }>(function CollabEditorBound(
  { conn, user, seed, onSnapshot, placeholder = "Add a description…", className, uploadTarget, editable = true },
  ref,
) {
  const [synced, setSynced] = useState(conn.synced);
  const snapshotRef = useRef(onSnapshot);
  snapshotRef.current = onSnapshot;
  const seedRef = useRef(seed);
  seedRef.current = seed;

  useEffect(() => {
    setSynced(conn.synced);
    return conn.subscribe(() => setSynced(true));
  }, [conn]);

  const editor = useEditor(
    {
      extensions: [...baseExtensions(placeholder, true), Collaboration.configure({ document: conn.doc, field: "default" }), CollaborationCaret.configure({ provider: conn, user })],
      editable,
      editorProps: {
        attributes: { class: "dayax-editor" },
        handlePaste: (_view, event) => {
          const files = [...(event.clipboardData?.files ?? [])];
          if (!files.length || !editorRef.current) return false;
          pasteImages(uploadTarget)(editorRef.current, files);
          return true;
        },
        handleDrop: (_view, event) => {
          const files = [...((event as DragEvent).dataTransfer?.files ?? [])];
          if (!files.length || !editorRef.current) return false;
          pasteImages(uploadTarget)(editorRef.current, files);
          return true;
        },
      },
    },
    [conn],
  );
  const editorRef = useRef<Editor | null>(null);
  editorRef.current = editor;
  useImperativeHandle(ref, () => ({ editor }), [editor]);

  useEffect(() => {
    if (!editor) return;
    conn.setSeedHandler(() => {
      if (seedRef.current) editor.commands.setContent(seedRef.current as never);
    });
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onUpdate = (_u: Uint8Array, origin: unknown) => {
      if (origin === conn) return; // remote change: whoever typed it saves the snapshot
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => snapshotRef.current(editor.getJSON() as RichText, editor.getText({ blockSeparator: "\n" }).trim()), 1200);
    };
    conn.doc.on("update", onUpdate);
    return () => {
      conn.doc.off("update", onUpdate);
      if (timer) {
        clearTimeout(timer);
        snapshotRef.current(editor.getJSON() as RichText, editor.getText({ blockSeparator: "\n" }).trim());
      }
    };
  }, [editor, conn]);

  return (
    <div className={cn("relative", className)}>
      {!synced && <div className="absolute inset-0 animate-soft-pulse rounded-md bg-muted/40" aria-label="Loading text" />}
      <EditorContent editor={editor} className={cn(!synced && "opacity-0")} />
    </div>
  );
});

// ---- comment editor ---------------------------------------------------------------------------------

export interface CommentEditorHandle {
  clear: () => void;
  focus: () => void;
  isEmpty: () => boolean;
  /** Calls onSubmit with the current content (same as ⌘↵). */
  submit: () => void;
  /** The Tiptap editor, for toolbars. */
  editor: Editor | null;
}

export const CommentEditor = forwardRef<
  CommentEditorHandle,
  {
    placeholder?: string;
    initial?: RichText;
    onSubmit: (json: RichText, text: string) => void;
    onTyping?: (typing: boolean) => void;
    onChange?: (json: RichText, text: string) => void;
    onFocusChange?: (focused: boolean) => void;
    autoFocus?: boolean;
    uploadTarget?: { issueId?: string };
    className?: string;
  }
>(function CommentEditor({ placeholder = "Leave a comment…", initial, onSubmit, onTyping, onChange, onFocusChange, autoFocus, uploadTarget, className }, ref) {
  const submitRef = useRef(onSubmit);
  submitRef.current = onSubmit;
  const typingRef = useRef(onTyping);
  typingRef.current = onTyping;
  const changeRef = useRef(onChange);
  changeRef.current = onChange;
  const editorRef = useRef<Editor | null>(null);
  const editor = useEditor({
    extensions: baseExtensions(placeholder, false),
    content: (initial as never) ?? "",
    autofocus: autoFocus ? "end" : false,
    editorProps: {
      attributes: { class: "dayax-editor min-h-[1.6em]" },
      handleKeyDown: (_view, event) => {
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && editorRef.current) {
          const ed = editorRef.current;
          if (!ed.isEmpty) submitRef.current(ed.getJSON() as RichText, ed.getText({ blockSeparator: "\n" }).trim());
          return true;
        }
        return false;
      },
      handlePaste: (_view, event) => {
        const files = [...(event.clipboardData?.files ?? [])];
        if (!files.length || !editorRef.current) return false;
        pasteImages(uploadTarget)(editorRef.current, files);
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => {
      typingRef.current?.(!ed.isEmpty);
      changeRef.current?.(ed.isEmpty ? null : (ed.getJSON() as RichText), ed.getText({ blockSeparator: "\n" }).trim());
    },
    onFocus: () => focusRef.current?.(true),
    onBlur: () => {
      typingRef.current?.(false);
      focusRef.current?.(false);
    },
  });
  const focusRef = useRef(onFocusChange);
  focusRef.current = onFocusChange;
  editorRef.current = editor;
  useImperativeHandle(
    ref,
    () => ({
      clear: () => editor?.commands.clearContent(true),
      focus: () => editor?.commands.focus("end"),
      isEmpty: () => editor?.isEmpty ?? true,
      submit: () => {
        if (editor && !editor.isEmpty) submitRef.current(editor.getJSON() as RichText, editor.getText({ blockSeparator: "\n" }).trim());
      },
      editor,
    }),
    [editor],
  );
  return <EditorContent editor={editor} className={className} />;
});

/** Read-only rendering of stored rich text (comments, updates). */
export function RichTextView({ content, className }: { content: RichText; className?: string }) {
  const editor = useEditor({ extensions: baseExtensions("", false, { slash: false }), content: (content as never) ?? "", editable: false, editorProps: { attributes: { class: "dayax-editor" } } }, [JSON.stringify(content)]);
  return <EditorContent editor={editor} className={className} />;
}

/** Plain text of each top-level list item in the selection, for "create issues from selection". */
export function selectedLines(editor: Editor): string[] {
  const { from, to } = editor.state.selection;
  const lines: string[] = [];
  editor.state.doc.nodesBetween(from, to, (node) => {
    if (node.type.name === "listItem" || node.type.name === "taskItem") {
      const t = node.textContent.trim();
      if (t) lines.push(t);
      return false;
    }
    if (node.type.name === "paragraph" && node.textContent.trim()) {
      lines.push(node.textContent.trim());
      return false;
    }
    return true;
  });
  return lines;
}
